// Search results context bar — Stream 3 PR-4c (2026-05-15).
//
// Sits above the kit grid when at least one nice-to-have preference
// is active. Tells the renter "47 kits match your dates and location
// · 12 offer catering · 18 offer balloons" — gives them a sense of
// how many listings already match their preferences without the
// preferences narrowing the result set.
//
// Hidden when no preferences are active (the normal MainPanelHeader
// "N kits match your search" copy stays in place there).

import React from 'react';
import classNames from 'classnames';

import {
  ADDON_CATEGORIES,
  findAddonCategory,
} from '../../../data/addon-categories';

import css from './SearchResultsContextBar.module.css';

// Friendly labels for the canonical aesthetic vocabulary. Anything
// not in the map falls through to a title-cased rendering of the
// raw value (e.g. 'beachcomber' → 'Beachcomber') so a future
// addition doesn't 404.
const AESTHETIC_LABELS = {
  boho: 'boho',
  modern: 'modern',
  vintage: 'vintage',
  garden: 'garden',
  coastal: 'coastal',
  minimalist: 'minimalist',
  romantic: 'romantic',
  rustic: 'rustic',
  elegant: 'elegant',
};

const niceLabel = (tier, value) => {
  if (tier === 'aesthetic') {
    return AESTHETIC_LABELS[value] || value;
  }
  // Add-on tiers: look up the category catalog for the display
  // label. Falls back to the raw value for unknown keys.
  const cat = findAddonCategory(value);
  return cat ? cat.label.toLowerCase() : value;
};

// Renders one fragment of the summary: "12 offer catering".
const fragmentFor = ({ tier, value, count }) => {
  const label = niceLabel(tier, value);
  if (tier === 'aesthetic') {
    return `${count} ${label}`;
  }
  return `${count} offer ${label}`;
};

const SearchResultsContextBar = props => {
  const { className, totalCount, matchSummary } = props;

  if (!matchSummary || matchSummary.length === 0) return null;

  const fragments = matchSummary.map(fragmentFor);

  return (
    <div className={classNames(css.root, className)} role="status" aria-live="polite">
      <span className={css.totalCount}>
        {totalCount} {totalCount === 1 ? 'kit' : 'kits'} match your dates and location
      </span>
      {fragments.map((text, i) => (
        <span key={`${i}-${text}`} className={css.fragment}>
          <span className={css.bullet} aria-hidden="true">
            ·
          </span>
          <span className={css.fragmentText}>{text}</span>
        </span>
      ))}
    </div>
  );
};

// Exported for tests.
export { niceLabel, fragmentFor, AESTHETIC_LABELS };

export default SearchResultsContextBar;
